/**
 * 외부 API — 등록 · 교체 팝업 (2026-09-21 · 시안 「외부 API 콘솔」).
 *
 * ## ★★★ 키는 넣을 때만 보인다
 *
 * 옛 키를 보여 주는 길이 **없다.** 바꾸는 길은 **새 키로 덮어쓰기**뿐이고, 비워 두면 그대로 둔다.
 * 마스킹된 값(`••••a1b2`)을 그대로 보내면 서버가 `MASKED_VALUE` 로 거절한다 — 화면 결함이 멀쩡한 키를 망가뜨리지 않게.
 *
 * ## 저장 전에 한 번 시험한다
 *
 * 「넣자마자 안 되는」 일을 막는 자리다. 저장하지 않고 판정만 받는다(`POST /admin/external-apis/test`).
 * ⚠ 그 호출도 **한도를 태운다** — 서버가 사용량에 적는다. 그래서 자동으로 부르지 않고 **누를 때만** 부른다.
 *
 * ## 회사 층은 읽기로만
 *
 * 이 범주를 **끈 회사**가 있으면 이름을 보여 준다(그 회사는 날씨가 안 나온다). 끄고 켜는 자리는 뒤로 미뤘다(시안).
 */
import { useEffect, useState } from 'react';
import { X, AlertTriangle } from '../../../icons';
import {
  createProvider, patchProvider, deleteProvider, testKey, listInstitutionSettings,
  type ExternalApiAdapter, type ExternalApiProvider, type InstitutionSetting, type ProbeResult,
} from '../../../api/externalApis';

export function errText(e: unknown, fallback: string): string {
  const r = (e as { response?: { data?: { message?: string; error?: string } } } | undefined)?.response?.data;
  return r?.message ?? r?.error ?? fallback;
}

const VERDICT_LABEL: Record<string, string> = {
  ok: '정상', auth_failed: '키 거절', shared_quota_exhausted: '한도 소진', provider_error: '공급자 오류', unreachable: '닿지 못함',
};

interface Props {
  /** null 이면 새로 등록. */
  provider: ExternalApiProvider | null;
  adapters: ExternalApiAdapter[];
  onClose: () => void;
  onSaved: (message: string) => void;
}

export function ExternalApiEditModal({ provider, adapters, onClose, onSaved }: Props) {
  const isNew = provider === null;
  // 등록할 수 있는 것은 «부를 줄 아는» 공급자뿐이다 — 이미 등록된 것은 뺀다.
  const choices = adapters.filter((a) => !a.registered);
  const [providerKey, setProviderKey] = useState(isNew ? (choices[0]?.provider_key ?? '') : provider.provider_key);
  const [apiKey, setApiKey] = useState('');
  const [quota, setQuota] = useState(provider?.daily_quota != null ? String(provider.daily_quota) : '');
  const [enabled, setEnabled] = useState(provider?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [offInstitutions, setOffInstitutions] = useState<InstitutionSetting[]>([]);

  const category = isNew ? choices.find((a) => a.provider_key === providerKey)?.category : provider.category;

  useEffect(() => {
    if (!category) return;
    let alive = true;
    listInstitutionSettings(category)
      .then((r) => { if (alive) setOffInstitutions(r.institutions.filter((i) => !i.usable && i.reason === 'institution_off')); })
      .catch(() => { /* 회사 층을 못 읽어도 팝업은 열린다 */ });
    return () => { alive = false; };
  }, [category]);

  const parsedQuota = quota.trim() === '' ? null : Number(quota.trim());
  const quotaInvalid = parsedQuota !== null && (!Number.isInteger(parsedQuota) || parsedQuota < 1);

  async function runTest() {
    if (!apiKey.trim()) { setError('시험할 키를 적으십시오.'); return; }
    setBusy(true); setError(null); setProbe(null);
    try {
      setProbe(await testKey(providerKey, apiKey.trim()));
    } catch (e: unknown) {
      setError(errText(e, '연결 시험에 실패했습니다'));
    } finally { setBusy(false); }
  }

  async function save() {
    if (quotaInvalid) { setError('하루 한도는 비우거나 1 이상의 정수로 적습니다.'); return; }
    setBusy(true); setError(null);
    try {
      if (isNew) {
        if (!providerKey) { setError('공급자를 고르십시오.'); setBusy(false); return; }
        await createProvider({
          provider_key: providerKey,
          enabled,
          daily_quota: parsedQuota,
          ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
        });
        onSaved('공급자를 등록했습니다.');
      } else {
        await patchProvider(provider.provider_key, {
          enabled,
          daily_quota: parsedQuota,
          // 비우면 «안 바꾼다» — 키를 지우는 것은 따로 눌러야 한다.
          ...(apiKey.trim() ? { api_key: apiKey.trim() } : {}),
        });
        onSaved('저장했습니다.');
      }
    } catch (e: unknown) {
      setError(errText(e, '저장하지 못했습니다'));
      setBusy(false);
    }
  }

  async function removeProvider() {
    if (isNew) return;
    setBusy(true); setError(null);
    try {
      await deleteProvider(provider.provider_key);
      onSaved('공급자를 지웠습니다.');
    } catch (e: unknown) {
      setError(errText(e, '지우지 못했습니다'));
      setBusy(false);
    }
  }

  return (
    <div className="cl-modal__backdrop" role="presentation" onMouseDown={onClose}>
      <div className="cl-modal" role="dialog" aria-modal="true" aria-label={isNew ? '공급자 등록' : provider.label} onMouseDown={(e) => e.stopPropagation()}>
        <div className="cl-modal__head">
          <h3>{isNew ? '공급자 등록' : provider.label}</h3>
          {!isNew && <span className={`xa-badge ${provider.enabled ? 'xa-badge--ok' : 'xa-badge--muted'}`}>{provider.enabled ? '켜짐' : '꺼짐'}</span>}
          <button type="button" className="cl-modal__close" aria-label="닫기" onClick={onClose}><X size={16} /></button>
        </div>

        <div className="cl-modal__body">
          {isNew
            ? (
              <div className="cl-field">
                <label htmlFor="xa-provider">공급자</label>
                <select id="xa-provider" className="xa-select" value={providerKey} onChange={(e) => setProviderKey(e.target.value)}>
                  {choices.length === 0 && <option value="">등록할 수 있는 공급자가 없습니다</option>}
                  {choices.map((a) => <option key={a.provider_key} value={a.provider_key}>{a.default_label}</option>)}
                </select>
                <p className="xa-hint">부를 줄 아는 공급자만 고를 수 있습니다 — 목록에 없으면 코드가 먼저 필요합니다.</p>
              </div>
            )
            : (
              <div className="cl-field">
                <label>지금 키</label>
                <div className="xa-readonly">
                  {provider.key_state === 'stored' && <b>{provider.key_hint}</b>}
                  {provider.key_state === 'none' && <span>등록된 키가 없습니다</span>}
                  {provider.key_state === 'unreadable' && <span className="xa-badge xa-badge--bad">키를 못 읽음 — 다시 넣어야 합니다</span>}
                </div>
                {provider.key_state === 'unreadable' && (
                  <p className="xa-hint">⚠ 암호를 푸는 열쇠가 바뀌었습니다. 이 상태에서는 <b>개발용 환경변수 키로 떨어지지 않습니다</b> — 조용히 도는 일을 막기 위해서입니다.</p>
                )}
              </div>
            )}

          <div className="cl-field">
            <label htmlFor="xa-key">{isNew ? '서비스 키' : '새 키로 교체'}</label>
            <input
              id="xa-key"
              className="xa-input"
              type="password"
              autoComplete="off"
              value={apiKey}
              placeholder={isNew ? '공급자에게 받은 인증키를 붙여 넣습니다' : '새 인증키 (비우면 그대로 둡니다)'}
              onChange={(e) => { setApiKey(e.target.value); setProbe(null); }}
            />
            <p className="xa-hint">
              {isNew
                ? '⚠ 저장하면 다시는 보이지 않습니다 — 끝 네 자리만 남습니다.'
                : '교체하면 옛 키는 사라집니다 — 되돌릴 수 없습니다.'}
            </p>
          </div>

          <div className="cl-field">
            <label htmlFor="xa-quota">하루 한도</label>
            <input id="xa-quota" className="xa-input" inputMode="numeric" value={quota} placeholder="예: 10000" onChange={(e) => setQuota(e.target.value)} />
            <p className="xa-hint">비우면 한도를 모르는 것으로 둡니다 — 90% 완충을 걸 수 없습니다.</p>
          </div>

          <label className="xa-check">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            이 공급자를 씁니다
          </label>

          {offInstitutions.length > 0 && (
            <div className="cl-field">
              <label>쓰지 않는 회사</label>
              <div className="xa-readonly">{offInstitutions.map((i) => i.name).join(' · ')}</div>
              <p className="xa-hint">회사 층에서 끈 곳입니다 — 그 회사는 이 기능이 나오지 않습니다.</p>
            </div>
          )}

          {/* ★ 운영자 카탈로그 구조를 «키를 넣는 순간»에 알려 준다. */}
          <div className="xa-band xa-band--warn">
            <AlertTriangle size={18} aria-hidden />
            <span className="xa-band__t">
              <b>전 회사가 이 키를 함께 씁니다</b>
              <span>한도에 닿으면 모든 회사가 함께 멈춥니다.</span>
            </span>
          </div>

          {probe && (
            <div className={`xa-band ${probe.verdict === 'ok' ? 'xa-band--ok' : 'xa-band--bad'}`} role="status">
              <AlertTriangle size={18} aria-hidden />
              <span className="xa-band__t">
                <b>{VERDICT_LABEL[probe.verdict] ?? probe.verdict}{probe.http_status ? ` · HTTP ${probe.http_status}` : ''}</b>
                <span>{probe.message}</span>
              </span>
            </div>
          )}
          {error && (
            <div className="xa-band xa-band--bad" role="alert">
              <AlertTriangle size={18} aria-hidden />
              <span className="xa-band__t"><b>{error}</b></span>
            </div>
          )}
        </div>

        <div className="cl-modal__foot">
          {!isNew && <button type="button" className="cl-modal__btn xa-danger" disabled={busy} onClick={() => void removeProvider()}>공급자 삭제</button>}
          <span className="xa-spacer" />
          <button type="button" className="cl-modal__btn" disabled={busy || !apiKey.trim() || !providerKey} onClick={() => void runTest()}>연결 시험</button>
          <button type="button" className="cl-modal__btn" disabled={busy} onClick={onClose}>취소</button>
          <button type="button" className="cl-modal__btn cl-modal__btn--primary" disabled={busy || quotaInvalid} onClick={() => void save()}>
            {isNew ? '등록' : '저장'}
          </button>
        </div>
      </div>
    </div>
  );
}
